/**
 * Stripe checkout-session request — what the client posts to
 * `POST /api/stripe/checkout-session` to mint a hosted Stripe Checkout
 * Session against the seller's Connect account.
 *
 * PR-L / Sibling-F · F13 (2026-06-27): the Stripe rail's wire shape was
 * collapsed onto the canonical {@link CheckoutPayload} from
 * `./checkout-payload.ts`. Pre-F13 this file declared a parallel
 * `StripeCheckoutData` interface + schema with a legacy flat `shippingInfo`
 * sub-object and no `kind` discriminator. The drift was the seed of two
 * money-bleed classes:
 *  - F13/PR-L money-fold: live_rate Stripe checkouts silently downgraded to
 *    flat_rate because the wire schema stripped `externalShipping` /
 *    `carrierProvider`, so `seller_orders.shipping = 0` + Kraahl paid the
 *    carrier label + the seller pocketed the shipping.
 *  - 2026-06-08 context drift: `StripeCheckoutData` required `offerId` on
 *    every checkout regardless of `kind`; cart-form clicks silently failed
 *    safeParse and the bare `catch{}` in `handleStripeCheckout` glomped it.
 *
 * One declaration across PayPal + Stripe (and the mobile builder once
 * `apps/mobile/lib/checkout-payload.ts::buildStripeCheckoutData` retires) =
 * the parallel-channel drift class is closed by construction.
 *
 * @see ./checkout-payload.ts — canonical {@link CheckoutPayload} (CAPTURE channel)
 * @see ./checkout-intent.ts — sibling {@link CheckoutIntent} (CREATE channel)
 * @see lib/payments/checkout-actions.ts — `createStripeCheckoutSession` builder
 * @see app/api/stripe/checkout-session/route.ts — server schema (this file's mirror)
 * @see .tondocs/shippo/HANDOFF_SIBLING_F_SWEEP_2026_06_26.md §3 — F13/PR-L scope
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-F · F13
 */

import { z } from "zod";
import { pinned } from "./pin";
import {
  CheckoutItemContextSchema,
  type CheckoutItemContext,
} from "./checkout-intent";
import { CheckoutPayloadSchema, type CheckoutPayload } from "./checkout-payload";

/**
 * Discriminator on the checkout-context — alias of the canonical
 * {@link CheckoutItemContext} (declared in `./checkout-intent`). Kept as a
 * back-compat re-export because the historical Stripe-rail name still flows
 * through one test (`tests/unit/checkout-context-roundtrip.test.ts`) which
 * documents the no-drift invariant across every downstream rail. Removing
 * the alias is a separate cleanup; the type is structurally identical.
 */
export type StripeCheckoutContext = CheckoutItemContext;

/**
 * Re-export the canonical schema under the Stripe-rail-historical name so
 * existing imports (`StripeCheckoutContextSchema`) keep resolving. Same Zod
 * object, single source — the pin lives on `CheckoutItemContextSchema` in
 * `./checkout-intent`; aliasing it here adds no new drift surface.
 */
// eslint-disable-next-line no-restricted-syntax -- alias-reexport of CheckoutItemContextSchema; the pin lives on the canonical declaration.
export const StripeCheckoutContextSchema = CheckoutItemContextSchema;

/**
 * Full request body for `POST /api/stripe/checkout-session`.
 *
 * Wraps the canonical {@link CheckoutPayload} so the Stripe rail consumes
 * the same discriminated `flat_rate | live_rate` shape PayPal consumes.
 * Per-request flags (idempotency key, return URL override, etc.) belong on
 * the wrapper, not on the payload — separation of money-shape from
 * transport keeps the wire compatible with future request-level extras.
 */
export interface CreateStripeCheckoutSessionRequest {
  checkoutData: CheckoutPayload;
}

// ---------------------------------------------------------------------------
// Runtime parse — Zod schemas
//
// Mirrors `app/api/stripe/checkout-session/route.ts::CheckoutBodySchema`.
// Single import for both clients and the route handler — the schema is the
// canonical entry point (no parallel inline declaration).
// ---------------------------------------------------------------------------

/**
 * Top-level request body. `.strict()` rejects unknown top-level fields so a
 * client accidentally sending `{ checkoutData, foo: 1 }` gets a 422 instead
 * of a silently-stripped extra. Inner objects (the payload itself) stay
 * loose-by-default — that's `CheckoutPayloadSchema`'s contract.
 */
export const CreateStripeCheckoutSessionRequestSchema =
  pinned<CreateStripeCheckoutSessionRequest>()(
    z
      .object({
        checkoutData: CheckoutPayloadSchema,
      })
      .strict(),
  );

// ---------------------------------------------------------------------------
// Response — discriminated union on `kind`
//
// Replaces the legacy parallel-optional-fields shape (`{ url?, alreadyPaid? }`)
// with two mutually-exclusive outcomes. Step 1 of
// `.tondocs/stripe/EMBEDDED_CHECKOUT_PLAN.md` — the route (Step 3) parses its
// own response through this schema before `apiJson` (producer-side wire pin,
// [[project_pr219_cutover_review]]), and downstream consumers (builder,
// embedded-checkout modal, `handleStripeCheckout`) branch on `kind` with a
// `never`-probe.
// ---------------------------------------------------------------------------

/**
 * Fresh mint, or a still-open reused draft session. Client mounts
 * `<PaymentElement>` + `<ExpressCheckoutElement>` under
 * `<CheckoutElementsProvider>` (from `@stripe/react-stripe-js/checkout`)
 * with `clientSecret`; `sellerStripeAccountId` scopes
 * `loadStripe(pk, { stripeAccount })` for direct charges — without it
 * Stripe.js would confirm against the platform account instead of the
 * seller's, and the PaymentIntent 404s.
 */
export interface CreateStripeCheckoutSessionMountResponse {
  kind: "mount";
  clientSecret: string;
  sessionId: string;
  sellerStripeAccountId: string;
}

/**
 * Session already completed (buyer double-click, or a reused draft whose
 * Stripe session moved past `open`). Client skips the iframe entirely and
 * routes straight to `/checkout/stripe-return?session_id=...`.
 */
export interface CreateStripeCheckoutSessionAlreadyPaidResponse {
  kind: "already_paid";
  sessionId: string;
}

/** Response body for `POST /api/stripe/checkout-session`. */
export type CreateStripeCheckoutSessionResponse =
  | CreateStripeCheckoutSessionMountResponse
  | CreateStripeCheckoutSessionAlreadyPaidResponse;

export const CreateStripeCheckoutSessionMountResponseSchema =
  pinned<CreateStripeCheckoutSessionMountResponse>()(
    z.object({
      kind: z.literal("mount"),
      clientSecret: z.string(),
      sessionId: z.string(),
      sellerStripeAccountId: z.string(),
    }),
  );

export const CreateStripeCheckoutSessionAlreadyPaidResponseSchema =
  pinned<CreateStripeCheckoutSessionAlreadyPaidResponse>()(
    z.object({
      kind: z.literal("already_paid"),
      sessionId: z.string(),
    }),
  );

/**
 * Combined ADT schema — single import for the route producer and every
 * downstream consumer added in later steps of the plan.
 */
export const CreateStripeCheckoutSessionResponseSchema =
  pinned<CreateStripeCheckoutSessionResponse>()(
    z.discriminatedUnion("kind", [
      CreateStripeCheckoutSessionMountResponseSchema,
      CreateStripeCheckoutSessionAlreadyPaidResponseSchema,
    ]),
  );
